import { FC, MutableRefObject, useEffect } from 'react';
import { BimSelection, BimTreeNode, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { BimSpatialTree } from 'Pages/Viewer/CoordlyViewer/3DViewer/components/BimSpatialTree';
import { useBim3DTools } from 'Pages/Viewer/CoordlyViewer/3DViewer/useBim3DTools';
import { IfcTreeWrapper } from './styles';

// Árvore + índice do modelo, como a página guarda por urn.
export interface IIfcModelTreeEntry {
  tree: BimTreeNode[];
  modelIndex?: number;
}

interface IIfcModelTree {
  /** Árvore DESTE modelo, já lida do motor pela página. */
  tree: BimTreeNode[];
  /** Índice do modelo no motor — endereça o data model e escopa a seleção. */
  modelIndex?: number;
  handleRef: MutableRefObject<BimViewerHandle | null>;
  search?: string;
  /**
   * Assina a seleção da cena. Sem isto a árvore só sabe do que ela mesma
   * seleciona: desmarcar no 3D (clique no vazio, Esc) deixava a linha marcada
   * apontando para um elemento que não estava mais em destaque.
   */
  subscribeSelection?(listener: (selection: BimSelection) => void): () => void;
}

// Árvore espacial de um modelo IFC dentro da lista de modelos do federado.
//
// Deliberadamente separada do `VirtualTreeView`: aquela é alimentada pelo
// `modelsTree` do Redux, que o InternalViewer preenche e limpa junto com
// `hideNodes` e `selectedNode`. O motor IFC escrevendo no mesmo estado disputaria
// com o caminho Autodesk.
//
// O comportamento é o do viewer único, não uma reimplementação: uma instância de
// `useBim3DTools` por modelo (o componente já é renderizado um por modelo) dá
// seleção, propriedades agregadas, expansão e visibilidade com a mesma lógica.
// O `modelIndex` é o que faz cada instância falar com o data model certo — sem
// ele tudo resolve contra o primeiro modelo da cena.
const IfcModelTree: FC<IIfcModelTree> = ({
  tree,
  modelIndex,
  handleRef,
  search = '',
  subscribeSelection,
}) => {
  const tools = useBim3DTools({ handleRef, modelIndex });
  const { handleSelectionChanged } = tools;

  // 3D → árvore. A cena é a fonte da verdade da seleção: o que ela desmarca sai
  // marcado daqui também.
  useEffect(() => {
    if (!subscribeSelection) { return; }
    return subscribeSelection((selection) => {
      // Seleção em OUTRO modelo desmarca esta árvore em vez de ser repassada: os
      // expressId são locais a cada arquivo, e repassar destacaria aqui o nó de
      // mesmo id — de um elemento que o usuário não tocou.
      const foreign = modelIndex !== undefined
        && selection.expressId !== null
        && selection.modelIndex !== modelIndex;
      handleSelectionChanged(foreign
        ? { expressId: null, modelIndex, expressIds: [] }
        : selection);
    });
  }, [subscribeSelection, handleSelectionChanged, modelIndex]);

  // A página é quem descobre que o data model ficou pronto (o evento chega no
  // hook da cena). Avisar aqui é o que faz o hook ler a árvore deste modelo e
  // montar os índices que a seleção usa.
  useEffect(() => {
    tools.handleDataModelReady({ available: tree.length > 0 });
    // Só quando a árvore deste modelo muda; `tools` troca de identidade a cada
    // mudança de estado interna e re-disparar aqui zeraria a expansão do usuário.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree]);

  if (tree.length === 0) {
    return null;
  }

  return (
    <IfcTreeWrapper>
      <BimSpatialTree
        tree={tools.tree}
        state={tools.dataModelState}
        expandedKeys={tools.expandedKeys}
        selectedKey={tools.selection?.treeKey}
        search={search}
        handle={handleRef.current}
        modelIndex={modelIndex}
        hiddenCountByKey={tools.hiddenCountByKey}
        hiddenIds={tools.hiddenIds}
        onExpandedKeysChange={tools.setExpandedKeys}
        onSelectNode={tools.selectTreeNode}
        onToggleVisibility={tools.toggleNodeVisibility}
      />
    </IfcTreeWrapper>
  );
};

export default IfcModelTree;
